import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { BottomNav } from "@/components/Nav";
import { Search, Plus, Trash2 } from "lucide-react";

function today() { return new Date().toISOString().slice(0,10); }

export default function Nutrition() {
  const [date, setDate] = useState(today());
  const [summary, setSummary] = useState(null);
  const [foods, setFoods] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [portion, setPortion] = useState(100);
  const [meal, setMeal] = useState("breakfast");
  const [err, setErr] = useState("");

  const load = async () => {
    const [s, f] = await Promise.all([
      api.get(`/food-log/summary?date=${date}`),
      api.get("/foods"),
    ]);
    setSummary(s.data); setFoods(f.data);
  };
  useEffect(() => { load(); }, [date]);

  const filtered = useMemo(() => {
    if (!query) return foods.slice(0, 30);
    const q = query.toLowerCase();
    return foods.filter(f => f.name.toLowerCase().includes(q)).slice(0, 30);
  }, [foods, query]);

  const addEntry = async () => {
    if (!selected) return;
    setErr("");
    const scale = portion/100;
    try {
      await api.post("/food-log", {
        date, meal, food_name: selected.name,
        portion_grams: portion,
        calories: Math.round(selected.calories*scale),
        protein_g: +(selected.protein_g*scale).toFixed(1),
        carbs_g: +(selected.carbs_g*scale).toFixed(1),
        fat_g: +(selected.fat_g*scale).toFixed(1),
      });
      setSelected(null); setPortion(100); setQuery("");
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const removeEntry = async (id) => {
    await api.delete(`/food-log/${id}`);
    load();
  };

  const [editing, setEditing] = useState(null); // {id, portion_grams}
  const saveEdit = async (entry, newPortion) => {
    const scale = newPortion / entry.portion_grams;
    await api.patch(`/food-log/${entry.id}`, {
      portion_grams: newPortion,
      calories: Math.round(entry.calories * scale),
      protein_g: +(entry.protein_g * scale).toFixed(1),
      carbs_g: +(entry.carbs_g * scale).toFixed(1),
      fat_g: +(entry.fat_g * scale).toFixed(1),
    });
    setEditing(null); load();
  };

  const byMeal = (summary?.entries || []).reduce((acc, e) => { (acc[e.meal] ||= []).push(e); return acc; }, {});

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white safe-pb">
      <div className="max-w-xl mx-auto px-4 pt-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display font-black uppercase text-3xl tracking-tighter">Nutrition</h1>
          <input type="date" className="kw-input w-auto" value={date} onChange={e=>setDate(e.target.value)} data-testid="food-date"/>
        </div>

        {summary && (
          <div className="kw-card mt-4 p-4" data-testid="macro-card">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Daily targets</div>
              {summary.coach_adjusted && (
                <div className="text-[10px] uppercase tracking-widest text-gold" data-testid="coach-targets-badge">Set by your coach</div>
              )}
            </div>
            <div className="font-display text-xl mt-1">{Math.round(summary.totals.calories)} <span className="text-neutral-500 text-base">/ {summary.targets.calories} kcal</span></div>
            <div className="h-1 bg-[#262626] mt-2"><div className="h-1 bg-gold" style={{width:`${Math.min(100,(summary.totals.calories/summary.targets.calories)*100)}%`}}/></div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              {["protein_g","carbs_g","fat_g"].map(k => (
                <div key={k} className="text-center">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">{k.replace("_g","")}</div>
                  <div className="font-display text-lg">{Math.round(summary.totals[k])}<span className="text-xs text-neutral-500">/{Math.round(summary.targets[k])}g</span></div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-[#262626]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Remaining today</div>
              <div className="grid grid-cols-4 gap-2 text-center" data-testid="remaining-row">
                {[
                  {k:"calories", label:"kcal", unit:""},
                  {k:"protein_g", label:"P", unit:"g"},
                  {k:"carbs_g", label:"C", unit:"g"},
                  {k:"fat_g", label:"F", unit:"g"},
                ].map(({k,label,unit}) => {
                  const r = summary.remaining?.[k];
                  const over = r !== undefined && r < 0;
                  return (
                    <div key={k} className="border border-[#262626] p-2" data-testid={`remaining-${k}`}>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
                      <div className={`font-display text-lg ${over?"text-red-400":"text-white"}`}>
                        {r === undefined ? "—" : over ? `+${Math.abs(Math.round(r))}${unit} over` : `${Math.round(r)}${unit}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="text-[10px] text-neutral-500 mt-3">Only your coach can change these targets.</div>
          </div>
        )}

        {/* Add food */}
        <div className="kw-card mt-5 p-4">
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Log food</div>
          <div className="grid grid-cols-4 gap-1 mb-3">
            {["breakfast","lunch","dinner","snack"].map(m => (
              <button key={m} onClick={()=>setMeal(m)} className={`py-1.5 border uppercase text-[10px] tracking-widest ${meal===m?"border-gold text-gold":"border-[#333] text-neutral-400"}`} data-testid={`meal-${m}`}>{m}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-3 text-neutral-500"/>
            <input className="kw-input pl-8" placeholder="Search taro, kumala, chicken…" value={query} onChange={e=>{setQuery(e.target.value); setSelected(null);}} data-testid="food-search"/>
          </div>
          {query && !selected && (
            <div className="mt-2 max-h-48 overflow-y-auto border border-[#262626]">
              {filtered.map((f,i) => (
                <button key={i} onClick={()=>{setSelected(f); setQuery(f.name);}} className="w-full text-left px-3 py-2 border-b border-[#1f1f1f] hover:border-gold text-sm" data-testid={`food-opt-${i}`}>
                  <div className="font-semibold">{f.name}</div>
                  <div className="text-xs text-neutral-500">{f.calories} kcal · P{f.protein_g} C{f.carbs_g} F{f.fat_g} / 100g</div>
                </button>
              ))}
              {filtered.length===0 && <div className="px-3 py-3 text-neutral-500 text-sm">No match. Create a custom food via your coach.</div>}
            </div>
          )}
          {selected && (
            <div className="mt-3 space-y-2">
              <div className="text-sm text-neutral-300">{selected.name}</div>
              <div className="flex items-center gap-2">
                <label className="text-xs uppercase tracking-widest text-neutral-500">Portion (g)</label>
                <input type="number" className="kw-input w-28" value={portion} onChange={e=>setPortion(parseFloat(e.target.value||"0"))} data-testid="food-portion"/>
                <button className="kw-btn-primary flex-1 flex items-center justify-center gap-1" onClick={addEntry} data-testid="food-add"><Plus className="w-4 h-4"/>Add</button>
              </div>
              <div className="text-xs text-neutral-500">{Math.round(selected.calories*(portion/100))} kcal · P{(selected.protein_g*(portion/100)).toFixed(1)} C{(selected.carbs_g*(portion/100)).toFixed(1)} F{(selected.fat_g*(portion/100)).toFixed(1)}</div>
            </div>
          )}
          {err && <div className="text-red-400 text-sm mt-2">{err}</div>}
        </div>

        {/* Logged */}
        <div className="mt-5 space-y-3" data-testid="food-log-list">
          {["breakfast","lunch","dinner","snack"].map(m => (
            byMeal[m]?.length ? (
              <div key={m} className="kw-card p-4">
                <div className="text-xs uppercase tracking-widest text-gold">{m}</div>
                {byMeal[m].map(e => (
                  <div key={e.id} className="flex items-center justify-between mt-2 py-2 border-t border-[#262626]">
                    <div className="flex-1">
                      <div className="text-sm">{e.food_name}</div>
                      {editing === e.id ? (
                        <div className="flex gap-2 items-center mt-1">
                          <input type="number" className="kw-input w-24 py-1" defaultValue={e.portion_grams} id={`ep-${e.id}`} data-testid={`food-edit-portion-${e.id}`}/>
                          <button className="text-gold text-xs uppercase tracking-widest" onClick={()=>saveEdit(e, parseFloat(document.getElementById(`ep-${e.id}`).value))} data-testid={`food-save-${e.id}`}>Save</button>
                          <button className="text-neutral-500 text-xs" onClick={()=>setEditing(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div className="text-xs text-neutral-500">{e.portion_grams}g · {Math.round(e.calories)} kcal · P{e.protein_g} C{e.carbs_g} F{e.fat_g}</div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={()=>setEditing(e.id)} className="text-neutral-500 hover:text-gold text-xs uppercase tracking-widest" data-testid={`food-edit-${e.id}`}>Edit</button>
                      <button onClick={()=>removeEntry(e.id)} className="text-neutral-500 hover:text-red-400" data-testid={`food-del-${e.id}`}><Trash2 className="w-4 h-4"/></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null
          ))}
          {(!summary?.entries?.length) && <div className="text-neutral-500 text-sm text-center py-8">No meals logged yet today.</div>}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
